'use client';

/**
 * Consent integration
 *
 * `useAnalytics()` is the public React hook. It subscribes to
 * `@unity/cookie-consent` `useConsent()` and lazily initializes PostHog
 * when `consent.analytics === true`. On revoke, it calls `revokePostHog()`
 * which opt-outs + clears cookies.
 *
 * Strict gating posture: PostHog is NEVER initialized unless consent has
 * been explicitly granted. Server-side: see server.ts for posthog-node
 * pattern that respects consent via session lookup.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useConsent } from '@unity/cookie-consent';
import type { AnalyticsConfig, AnalyticsEventName, EventProps, IdentifyProps } from './types';
import { initPostHog, capturePostHog, identifyPostHog, revokePostHog, grantPostHog } from './posthog';

export interface UseAnalyticsResult {
  /** Capture an event. NO-OP when consent denied or PostHog not configured. */
  capture: (event: AnalyticsEventName, props?: Partial<EventProps>) => void;
  /** Identify the current user. NO-OP when consent denied. */
  identify: (props: IdentifyProps) => void;
  /** Whether analytics is currently active (consent granted + initialized). */
  enabled: boolean;
}

/**
 * Read PostHog config from `NEXT_PUBLIC_POSTHOG_KEY` and
 * `NEXT_PUBLIC_POSTHOG_HOST` env vars. Falls back to PostHog Cloud US.
 * Returns a config with `apiKey: undefined` when vars are unset — in
 * which case all subsequent capture calls are NO-OPs.
 */
function readEnvConfig(appName: AnalyticsConfig['appName']): AnalyticsConfig {
  const g = globalThis as { process?: { env?: Record<string, string | undefined> } };
  const env = g.process?.env ?? {};
  return {
    apiKey: env.NEXT_PUBLIC_POSTHOG_KEY || env.POSTHOG_PROJECT_API_KEY || undefined,
    host: env.NEXT_PUBLIC_POSTHOG_HOST || env.POSTHOG_HOST || 'https://us.i.posthog.com',
    appName,
    debug: env.NODE_ENV !== 'production',
  };
}

/**
 * Primary hook. Subscribe to consent + return capture/identify functions
 * that respect the consent gate.
 *
 * @param appName Which Unity app is calling — tags every event.
 */
export function useAnalytics(appName: AnalyticsConfig['appName']): UseAnalyticsResult {
  const { consent, hasConsent } = useConsent();
  const initStarted = useRef(false);
  const lastConsentState = useRef<boolean | null>(null);

  const config = useMemo(() => readEnvConfig(appName), [appName]);
  const enabled = hasConsent('analytics');

  useEffect(() => {
    // Track consent transitions: null → granted, granted → revoked, etc.
    const currentGranted = enabled;
    const prevGranted = lastConsentState.current;

    if (currentGranted && !prevGranted) {
      // GRANT: init (idempotent) + opt_in
      if (!initStarted.current) {
        initStarted.current = true;
        void initPostHog(config).then((ok) => {
          if (ok) grantPostHog();
        });
      } else {
        grantPostHog();
      }
    } else if (!currentGranted && prevGranted === true) {
      // REVOKE: opt_out + reset
      revokePostHog();
    }

    lastConsentState.current = currentGranted;
  }, [enabled, config, consent]);

  const capture = useCallback(
    (event: AnalyticsEventName, props?: Partial<EventProps>) => {
      if (!enabled) return;
      capturePostHog(event, { ...(props ?? {}), app_name: appName } as EventProps);
    },
    [enabled, appName],
  );

  const identify = useCallback(
    (props: IdentifyProps) => {
      if (!enabled) return;
      identifyPostHog(props);
    },
    [enabled],
  );

  return { capture, identify, enabled };
}
